"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";

const DashboardQuery = graphql(`
  query EditDashboardConfirmQuery($id: ID!) {
    node(id: $id) {
      __typename
      ... on Dashboard {
        id
        name
        description
      }
    }
  }
`);

type EditDashboardInput = {
  dashboardId: string;
  name?: string;
  description?: string;
};

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function EditDashboardConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget = isToolUIPart(part) && getToolName(part) === "edit_dashboard";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<EditDashboardInput>;
  const dashboardId = input.dashboardId ?? "";

  const [{ data, fetching }] = useQuery({
    query: DashboardQuery,
    variables: { id: dashboardId },
    pause: !isTarget || !isInputAvailable || !dashboardId,
  });

  if (!isTarget || !isInputAvailable) return null;

  const node = data?.node;
  const dashboard = node?.__typename === "Dashboard" ? node : null;
  const pending = fetching || !dashboard;

  const oldName = dashboard?.name ?? "";
  const oldDescription =
    typeof dashboard?.description === "string" ? dashboard.description : "";

  const newName = typeof input.name === "string" ? input.name : undefined;
  const newDescription =
    typeof input.description === "string" ? input.description : undefined;

  const nameChanged =
    !!newName &&
    newName.trim() !== "" &&
    newName.trim() !== (oldName ?? "").trim();
  const descriptionChanged =
    typeof newDescription === "string" && newDescription !== oldDescription;

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update this dashboard?</Trans>
      </div>

      <ul className="mb-4 list-disc space-y-2 pl-5">
        <li>
          <Trans>Name: </Trans>
          {pending ? (
            <Skeleton className="inline-block h-4 w-24 align-middle bg-gray-400 dark:bg-gray-600" />
          ) : (
            <>
              <span>{oldName || "-"}</span>
              {nameChanged ? (
                <span className="ml-2 font-semibold">→ {newName}</span>
              ) : (
                <span className="ml-2">
                  (<Trans>No changes</Trans>)
                </span>
              )}
            </>
          )}
        </li>

        <li>
          <div className="mb-1">
            <Trans>Description:</Trans>
            {!pending && !descriptionChanged ? (
              <span className="ml-2">
                (<Trans>No changes</Trans>)
              </span>
            ) : null}
          </div>
          {pending ? (
            <Skeleton className="h-10 w-full max-w-md bg-gray-400 dark:bg-gray-600" />
          ) : descriptionChanged ? (
            <div className="rounded border bg-muted/30 p-2 text-xs">
              <div>
                <span className="mr-2">
                  <Trans>Current:</Trans>
                </span>
                <span>{oldDescription || "-"}</span>
              </div>
              <div className="mt-1">
                <span className="mr-2">
                  <Trans>New:</Trans>
                </span>
                <span className="font-semibold">{newDescription || "-"}</span>
              </div>
            </div>
          ) : null}
        </li>
      </ul>

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
